/**
 * ACRES Admin — Moderation Queue
 * Multi-moderator claim/release with 15-min TTL, decision UI, two-step confirm for L4-L5 / >=500 pts.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { acresAdmin } from '../../../lib/services/acresService';

const PRESET_REJECT = [
  'Proof unclear / unreadable',
  'Proof does not match task requirements',
  'Duplicate submission detected',
  'Account / handle does not match registered profile',
  'Task not actually completed',
  'Suspected automated / bot activity',
  'Other (see notes)'
];

interface Submission {
  id: string; user_id: number; template_id: string;
  template_title?: string; template_level?: number; template_points?: number;
  template_requires_two_step?: boolean;
  status: string; proof_url?: string; proof_text?: string; proof_mime?: string;
  claimed_by?: number | null; claimed_until?: string | null;
  submitted_at: string; user_handle?: string;
  approval_count?: number; awaiting_second_approval?: boolean;
}

export const AcresModeration: React.FC<{ currentUser?: any }> = ({ currentUser }) => {
  const [items, setItems] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'pending' | 'mine' | 'awaiting_second'>('pending');
  const [active, setActive] = useState<Submission | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState(PRESET_REJECT[0]);
  const [error, setError] = useState<string | null>(null);

  const myAdminId = currentUser?.adminUser?.id || currentUser?.id;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await acresAdmin.listQueue({ filter, limit: 50 }); setItems(r.submissions || []); }
    catch (e: any) { setError(e?.message || 'Failed to load queue'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openSubmission = async (s: Submission) => {
    setActive(s); setDecisionNotes(''); setHistory([]);
    try { const r = await acresAdmin.history(s.id); setHistory(r.history || []); } catch { /* non-fatal */ }
  };

  const claim = async (s: Submission) => {
    try { await acresAdmin.claim(s.id, 15); await load(); await openSubmission(s); }
    catch (e: any) { alert(e?.message || 'Claim failed'); }
  };
  const release = async (s: Submission) => {
    try { await acresAdmin.release(s.id); await load(); setActive(null); }
    catch (e: any) { alert(e?.message || 'Release failed'); }
  };
  const approve = async () => {
    if (!active) return;
    const requiresTwo = active.template_requires_two_step || (active.template_level || 0) >= 4 || (active.template_points || 0) >= 500;
    const confirmMsg = requiresTwo
      ? `This task requires TWO moderator approvals (L${active.template_level}, ${active.template_points} pts).\nYour approval will ${active.awaiting_second_approval ? 'finalize the second approval and credit points' : 'be recorded as the first approval; a second moderator must confirm before points are credited'}.\n\nProceed?`
      : `Approve this submission and credit ${active.template_points} points to the user?`;
    if (!confirm(confirmMsg)) return;
    try { await acresAdmin.approve(active.id, decisionNotes || undefined); setActive(null); await load(); }
    catch (e: any) { alert(e?.message || 'Approve failed'); }
  };
  const reject = async () => {
    if (!active) return;
    if (!rejectReason) { alert('Select a reason'); return; }
    if (!confirm(`Reject submission with reason: "${rejectReason}"?`)) return;
    try { await acresAdmin.reject(active.id, rejectReason + (decisionNotes ? `\n\nNotes: ${decisionNotes}` : '')); setActive(null); await load(); }
    catch (e: any) { alert(e?.message || 'Reject failed'); }
  };
  const requestRevision = async () => {
    if (!active || !decisionNotes.trim()) { alert('Enter revision notes for the user'); return; }
    try { await acresAdmin.requestRevision(active.id, decisionNotes.trim()); setActive(null); await load(); }
    catch (e: any) { alert(e?.message || 'Request revision failed'); }
  };
  const skip = async () => {
    if (!active) return;
    const reason = prompt('Reason for skipping (logged for audit)?', 'Outside my expertise / region');
    if (!reason) return;
    try { await acresAdmin.skip(active.id, reason); setActive(null); await load(); }
    catch (e: any) { alert(e?.message || 'Skip failed'); }
  };

  const isMine = (s: Submission) => s.claimed_by && Number(s.claimed_by) === Number(myAdminId);
  const claimedByOther = (s: Submission) => s.claimed_by && !isMine(s) && s.claimed_until && new Date(s.claimed_until) > new Date();

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['pending','mine','awaiting_second'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...btn, ...(filter === f ? btnActive : {}) }}>
            {f === 'pending' ? 'Pending Queue' : f === 'mine' ? 'My Claims' : 'Awaiting 2nd Approval'}
          </button>
        ))}
        <button onClick={load} style={btn}>↻ Refresh</button>
      </div>
      {error && <div style={errBox}>{error}</div>}
      {loading && <div style={{ color: '#9CA3AF' }}>Loading…</div>}

      <div style={{ display: 'grid', gridTemplateColumns: active ? '1fr 1.4fr' : '1fr', gap: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr style={trHead}>
              <th style={th}>Submitted</th><th style={th}>User</th><th style={th}>Task</th>
              <th style={th}>Pts</th><th style={th}>Status</th><th style={th}>Lock</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} style={trBody}>
                  <td style={td}>{new Date(s.submitted_at).toLocaleString()}</td>
                  <td style={td}>{s.user_handle || `#${s.user_id}`}</td>
                  <td style={td}>L{s.template_level} · {s.template_title}</td>
                  <td style={td}>{s.template_points}</td>
                  <td style={td}>{s.awaiting_second_approval ? '⏳ 2nd' : s.status}</td>
                  <td style={td}>{isMine(s) ? '🟡 you' : claimedByOther(s) ? '🔒 other' : '—'}</td>
                  <td style={td}>
                    {!s.claimed_by || (s.claimed_until && new Date(s.claimed_until) <= new Date())
                      ? <button onClick={() => claim(s)} style={btnSmall}>Claim</button>
                      : isMine(s) ? <button onClick={() => openSubmission(s)} style={btnSmall}>Open</button> : <span style={{ color: '#6B7280' }}>locked</span>}
                  </td>
                </tr>
              ))}
              {!items.length && !loading && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Queue empty</td></tr>}
            </tbody>
          </table>
        </div>

        {active && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 700 }}>L{active.template_level} · {active.template_title} · {active.template_points} pts</div>
              <button onClick={() => setActive(null)} style={btnSmall}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>User: {active.user_handle || `#${active.user_id}`} · Submitted: {new Date(active.submitted_at).toLocaleString()}</div>
            {active.proof_url && <ProofPreview url={active.proof_url} mime={active.proof_mime} />}
            {active.proof_text && <div style={proofText}>{active.proof_text}</div>}
            <div style={{ marginTop: 10 }}>
              <select value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={input}>
                {PRESET_REJECT.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <textarea placeholder="Decision / revision notes (visible to user on reject/revision)" value={decisionNotes} onChange={e => setDecisionNotes(e.target.value)} style={{ ...input, minHeight: 60, marginTop: 8 }} />
            </div>
            {(active.template_requires_two_step || (active.template_level || 0) >= 4 || (active.template_points || 0) >= 500) && (
              <div style={warnBox}>⚠ This task requires TWO moderator approvals before points are credited.</div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button onClick={approve} style={{ ...btn, background: '#16A34A', color: '#FFF', border: 'none' }}>✓ Approve</button>
              <button onClick={reject} style={{ ...btn, background: '#DC2626', color: '#FFF', border: 'none' }}>✗ Reject</button>
              <button onClick={requestRevision} style={btn}>↻ Request Revision</button>
              <button onClick={skip} style={btn}>⏭ Skip</button>
              <button onClick={() => release(active)} style={btn}>Release Lock</button>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#9CA3AF' }}>History ({history.length})</div>
            <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12 }}>
              {history.map((h, i) => <div key={i} style={{ borderBottom: '1px solid #1E1E1E', padding: '4px 0' }}>{new Date(h.created_at).toLocaleString()} · {h.action} · admin#{h.admin_id} {h.notes ? `· ${h.notes}` : ''}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProofPreview: React.FC<{ url: string; mime?: string }> = ({ url, mime }) => {
  const isImg = (mime || '').startsWith('image/');
  const isVid = (mime || '').startsWith('video/');
  const isPdf = (mime || '').includes('pdf');
  return (
    <div style={{ border: '1px solid #1E1E1E', borderRadius: 8, padding: 8, background: '#0A0A0A', textAlign: 'center' }}>
      {isImg && <img src={url} alt="proof" style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }} />}
      {isVid && <video src={url} controls style={{ maxWidth: '100%', maxHeight: 360 }} />}
      {isPdf && <iframe src={url} style={{ width: '100%', height: 400, border: 0 }} />}
      {!isImg && !isVid && !isPdf && <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#F59E0B' }}>Open proof attachment ↗</a>}
    </div>
  );
};

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px' };
const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 14 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13 };
const btn: React.CSSProperties = { padding: '6px 12px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const btnActive: React.CSSProperties = { borderColor: '#F59E0B', color: '#F59E0B' };
const btnSmall: React.CSSProperties = { padding: '4px 10px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
const warnBox: React.CSSProperties = { padding: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid #F59E0B', borderRadius: 6, color: '#FBBF24', fontSize: 12, marginTop: 8 };
const proofText: React.CSSProperties = { padding: 8, background: '#0A0A0A', border: '1px solid #1E1E1E', borderRadius: 6, marginTop: 8, fontSize: 13, whiteSpace: 'pre-wrap' };
